// Achilles Tendon Template Configuration
// Questions simplified to 12-year-old reading level

export const achillesTemplate = {
  id: 'ACHILLES',
  name: 'Achilles Tendon',
  icon: '🦶',
  dominantSide: 'leg',

  fields: {
    injuredSide: {
      type: 'radio',
      label: 'Which ankle/heel has the problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    injuryDate: {
      type: 'date',
      label: 'When did this problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did the problem start?',
      options: ['Suddenly during activity', 'Slowly over time'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Pain at the back of my heel/ankle',
        'Swelling',
        'Stiffness',
        'Weakness',
        'A bump or lump on my tendon',
        'A snapping feeling',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Other problem (please describe):',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how the problem started and what you were doing',
      rows: 3,
      required: true
    },

    // Pain Section
    painLocation: {
      type: 'radio',
      label: 'Where exactly is your pain?',
      options: [
        'Right at the heel bone',
        'In the middle of the tendon (above the heel)',
        'Higher up where the muscle meets the tendon',
        'All along the tendon'
      ]
    },

    painType: {
      type: 'checkbox-multiple',
      label: 'What does the pain feel like?',
      options: ['Sharp', 'Aching', 'Burning', 'Throbbing', 'Other']
    },

    painTypeOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painType', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painTiming: {
      type: 'checkbox-multiple',
      label: 'When is your pain worst?',
      options: [
        'First thing in the morning',
        'During exercise',
        'After exercise',
        'All the time',
        'At night'
      ]
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Running',
        'Jumping',
        'Walking up stairs',
        'Going up hills',
        'Standing on tip-toes',
        'Squatting down',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Ice',
        'Putting my feet up',
        'Pain medication',
        'Wearing a heel lift',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Swelling Section
    hasSwelling: {
      type: 'radio',
      label: 'Do you have any swelling?',
      options: ['Yes', 'No']
    },

    swellingLocation: {
      type: 'text',
      label: 'Where is the swelling?',
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    swellingChange: {
      type: 'radio',
      label: 'Is the swelling:',
      options: ['Getting worse', 'Getting better', 'Staying the same'],
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    hasLump: {
      type: 'radio',
      label: 'Is there a lump or bump on your tendon?',
      options: ['Yes', 'No']
    },

    hasRedness: {
      type: 'radio',
      label: 'Is the area red or warm to touch?',
      options: ['Yes', 'No']
    },

    // Stiffness Section
    hasStiffness: {
      type: 'radio',
      label: 'Does your Achilles tendon feel stiff?',
      options: ['Yes', 'No']
    },

    stiffnessTiming: {
      type: 'checkbox-multiple',
      label: 'When is the stiffness worst?',
      options: ['In the morning', 'After sitting/resting', 'After exercise'],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    stiffnessChange: {
      type: 'radio',
      label: 'Is the stiffness:',
      options: ['Getting better', 'Getting worse', 'Staying the same'],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    // Rupture/Pop Questions
    feltPop: {
      type: 'radio',
      label: 'Did you feel or hear a sudden "pop" or "snap"?',
      options: ['Yes', 'No']
    },

    suddenWeakness: {
      type: 'radio',
      label: 'Did you suddenly lose the ability to push off with your foot?',
      options: ['Yes', 'No'],
      showWhen: { field: 'feltPop', equals: 'Yes' }
    },

    couldWalkAfter: {
      type: 'radio',
      label: 'Were you able to walk right after it happened?',
      options: ['Yes', 'No'],
      showWhen: { field: 'feltPop', equals: 'Yes' }
    },

    // Functional Limitations
    walkingDifficulty: {
      type: 'radio',
      label: 'Do you have trouble walking normally?',
      options: ['Yes', 'No', 'Sometimes']
    },

    stairsDifficulty: {
      type: 'radio',
      label: 'Do you have trouble with stairs?',
      options: ['Yes', 'No', 'Sometimes']
    },

    tiptoeDifficulty: {
      type: 'radio',
      label: 'Can you stand on tip-toes on the sore side?',
      options: ['Yes, easily', 'Yes, but it hurts', 'No, I can\'t do it']
    },

    runningDifficulty: {
      type: 'radio',
      label: 'Can you run?',
      options: ['Yes, no problem', 'Yes, but it hurts', 'No']
    },

    jumpingDifficulty: {
      type: 'radio',
      label: 'Can you jump?',
      options: ['Yes, no problem', 'Yes, but it hurts', 'No']
    },

    // Previous History
    previousAchillesProblems: {
      type: 'radio',
      label: 'Have you had Achilles tendon problems before?',
      options: ['Yes', 'No']
    },

    previousAchillesDetails: {
      type: 'textarea',
      label: 'Tell us about your previous Achilles problems:',
      rows: 2,
      showWhen: { field: 'previousAchillesProblems', equals: 'Yes' }
    },

    previousAchillesSurgery: {
      type: 'radio',
      label: 'Have you ever had surgery on your Achilles tendon?',
      options: ['Yes', 'No']
    },

    footProblems: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these foot problems?',
      options: [
        'Flat feet',
        'High arches',
        'Tight calf muscles',
        'None of these',
        'Not sure'
      ]
    },

    // Medical History
    medicalConditions: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these conditions?',
      options: [
        'Diabetes',
        'Gout',
        'Rheumatoid arthritis',
        'High cholesterol',
        'None of these'
      ]
    },

    takingSteroidsOrAntibiotics: {
      type: 'radio',
      label: 'Have you recently taken steroid medication (like prednisone) or certain antibiotics (like ciprofloxacin)?',
      options: ['Yes', 'No', 'Not sure']
    },

    currentMedications: {
      type: 'textarea',
      label: 'What medications do you take regularly?',
      placeholder: 'List all your current medications',
      rows: 2
    },

    allergies: {
      type: 'text',
      label: 'Do you have any allergies?',
      placeholder: 'List any allergies to medications or other things'
    },

    // Lifestyle
    occupation: {
      type: 'text',
      label: 'What is your job?',
      placeholder: 'Tell us what you do for work'
    },

    sport: {
      type: 'text',
      label: 'What sports or exercise do you do?',
      placeholder: 'e.g., running, tennis, basketball'
    },

    recentTrainingChange: {
      type: 'radio',
      label: 'Have you recently increased how much exercise you do?',
      options: ['Yes', 'No']
    },

    footwear: {
      type: 'text',
      label: 'What kind of shoes do you usually wear?',
      placeholder: 'e.g., runners, work boots, flat shoes'
    },

    smoking: {
      type: 'radio',
      label: 'Do you smoke?',
      options: ['No, never smoked', 'I used to smoke', 'Yes, I smoke']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'injury',
      title: '2. About Your Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injuredSide', 'onsetType', 'mainProblem', 'mainProblemOther', 'injuryDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painType', 'painTypeOther', 'painSeverity', 'painTiming', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'swelling',
      title: '4. Swelling & Changes',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasSwelling', 'swellingLocation', 'swellingChange', 'hasLump', 'hasRedness']
    },
    {
      id: 'stiffness',
      title: '5. Stiffness',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasStiffness', 'stiffnessTiming', 'stiffnessChange']
    },
    {
      id: 'rupture',
      title: '6. Did Something Tear?',
      component: 'GenericSection',
      enabled: true,
      fields: ['feltPop', 'suddenWeakness', 'couldWalkAfter']
    },
    {
      id: 'functional',
      title: '7. What You Can & Can\'t Do',
      component: 'GenericSection',
      enabled: true,
      fields: ['walkingDifficulty', 'stairsDifficulty', 'tiptoeDifficulty', 'runningDifficulty', 'jumpingDifficulty']
    },
    {
      id: 'history',
      title: '8. Previous Problems',
      component: 'GenericSection',
      enabled: true,
      fields: ['previousAchillesProblems', 'previousAchillesDetails', 'previousAchillesSurgery', 'footProblems']
    },
    {
      id: 'medical',
      title: '9. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true
    },    {
      id: 'exerciseVitalSign',
      title: '10. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '11. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly during activity': 'acute onset during activity',
      'Slowly over time': 'gradual onset'
    },
    commonDiagnoses: [
      'Achilles tendinopathy',
      'Achilles tendon rupture',
      'Insertional Achilles tendinopathy',
      'Retrocalcaneal bursitis',
      'Paratenonitis'
    ]
  }
};

export default achillesTemplate;
